import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { NotificationType, sendNotification } from '@tk/utilities/tk.notification';
import { compareMessages } from '@tk/utilities/tk.messages';
import TKCompareStore, { Category } from './tk.compare.store';
import { useStore, waitForStore } from '@tk/utilities/tk.use.store';

export enum CompareButtonType {
    ADD = 'add',
    REMOVE = 'remove',
    REMOVE_ALL = 'removeAll',
}

export default class TKCompareButton extends TKCustomElementFactory {
    store?: TKCompareStore;
    button?: HTMLButtonElement;
    activeClass: string;
    articleId: number;
    categoryId: number;
    categoryName?: string;
    type: CompareButtonType;
    protected unsubscribe?: () => void;

    constructor() {
        super();

        this.button = this.querySelector('button') || undefined;
        this.activeClass = this.getAttribute('data-tk-compare-active-class') || 'tk-compare-button--active';
        this.articleId = Number(this.getAttribute('data-tk-article-id') || 0);
        this.categoryId = Number(this.getAttribute('data-tk-category-id') || 0);
        this.categoryName = this.getAttribute('data-tk-category-name') || undefined;
        this.type = (this.getAttribute('data-tk-type') as CompareButtonType) || CompareButtonType.ADD;
    }

    async connectedCallback() {
        if (!this.button) throw new Error('Compare: Button is missing!');
        await waitForStore('compareStore');
        this.store = useStore('compareStore') as TKCompareStore;
        this.initializeDatabase();
        this.registerClickListener();
    }

    disconnectedCallback(): void {
        this.unsubscribe && this.unsubscribe();
    }

    registerClickListener() {
        this.pushListener({
            event: 'click',
            element: this,
            action: this.runActionByType.bind(this),
        });
    }

    async runActionByType() {
        if (!this.store) return;
        const handler: Record<CompareButtonType, () => void> = {
            add: this.toggle.bind(this),
            remove: this.remove.bind(this),
            removeAll: this.clearAll.bind(this),
        };
        handler[this.type]();
    }

    initializeDatabase() {
        if (!this.store) return;
        this.unsubscribe = this.store.subscribe(() => this.render());
        this.render();
    }

    clearAll() {
        if (!this.store) return;
        this.button!.disabled = true;
        this.store.clearAll().then(() => {
            sendNotification(NotificationType.SUCCESS, compareMessages.allRemoved.text);
            this.button!.disabled = false;
        });
    }

    async remove() {
        if (!this.categoryId || !this.articleId || !this.store) throw new Error('Category or article id is missing!');
        const category = this.store.getCategoryById(this.categoryId);
        if (!category) return;
        const filteredArticles = category.articleIds.filter((id) => id !== this.articleId);
        if (filteredArticles.length === 0) {
            await this.store.removeCategoryById(this.categoryId);
            const categories = this.store?.getState().categories;
            this.store?.switchCategory(categories?.at(0)?.id || 0);
        } else {
            category.articleIds = filteredArticles;
            await this.store.patchCategoryById(this.categoryId, category);
        }
    }

    async toggle() {
        if (!this.categoryId || !this.articleId || !this.store) throw new Error('Category or article id is missing!');

        const category = this.store.getCategoryById(this.categoryId);

        if (category) {
            await this.updateExistingCategory(category);
        } else {
            await this.createNewCategory();
        }
    }

    protected async updateExistingCategory(category: Category) {
        const articleExists = category.articleIds.includes(this.articleId);

        if (articleExists) {
            category.articleIds = category.articleIds.filter((id) => id !== this.articleId);
            const articleCount = this.store?.getArticleCountByCategoryId(category.id);

            if (articleCount === 0) {
                await this.store?.removeCategoryById(this.categoryId);
            } else {
                await this.store?.patchCategoryById(this.categoryId, category);
            }

            sendNotification(NotificationType.SUCCESS, compareMessages.removed.text);
        } else {
            category.articleIds.push(this.articleId);
            await this.store?.patchCategoryById(this.categoryId, category);
            sendNotification(NotificationType.SUCCESS, compareMessages.added.text);
        }
    }

    protected async createNewCategory() {
        const newCategory: Category = {
            id: this.categoryId,
            name: this.categoryName || '',
            articleIds: [this.articleId],
        };
        await this.store?.patchCategoryById(this.categoryId, newCategory);
        sendNotification(NotificationType.SUCCESS, compareMessages.added.text);
    }

    checkAvailability() {
        if (!this.store) return;
        const { currentCategoryId } = this.store.getState();
        const currentCategory = this.store.getCategoryById(currentCategoryId);
        this.button!.hidden = !currentCategory;
    }

    render() {
        if (this.type === CompareButtonType.ADD) {
            const category = this.store?.getCategoryById(this.categoryId);
            if (!category) {
                this.classList.remove(this.activeClass);
                return;
            }
            this.classList.toggle(this.activeClass, category.articleIds.includes(this.articleId));
        } else if (this.type === CompareButtonType.REMOVE_ALL) {
            this.checkAvailability();
        }
    }
}
